import axios from 'axios';
import { logError } from './utils/envUtils';
import { getCurrentSelectedOrganizationId } from './utils/loginUtils';

/**
 *
 * @param config
 * @returns {boolean}
 */
const isHandlerEnabled = (config = {}) => {
    return !(Object.prototype.hasOwnProperty.call(config, 'handlerEnabled') && !config.handlerEnabled);
};

/**
 * Global error handler.
 *
 * @param error
 * @returns {Promise<never>}
 */
const errorHandlerResponse = async (error) => {
    if (isHandlerEnabled(error.config)) {
        logError(error);
    }

    // A single 401 is not proof the session is over (DEV-5375): server-side token refresh is
    // single-flighted across service pods, and a request that lands mid-refresh can time out with a
    // transient 401 while the session is perfectly healthy. Retry ONCE after a short pause — by then
    // the refreshed token is saved and the retry authenticates normally. Only a 401 on the retry
    // (or a 403, which no retry will improve) means the user genuinely has no access.
    if (error.response?.status === 401 && error.config && !error.config._retriedAfter401) {
        await new Promise((resolve) => setTimeout(resolve, 1500));

        try {
            return await axios.request({ ...error.config, _retriedAfter401: true });
        } catch (retryError) {
            error = retryError;
        }
    }

    // Redirect to my profile on unauthorized
    if (error.response?.status === 401 || error.response?.status === 403) {
        window.location.assign(`${process.env['REACT_APP_INTELY_UI_EXTERNAL_HOST']}/my-profile`);

        return Promise.resolve();
    }

    return Promise.reject({ ...error });
};

/**
 * Global success handler.
 *
 * @param response
 * @returns {*}
 */
const successHandler = (response) => {
    if (isHandlerEnabled(response.config)) {
        // Handle responses
    }

    return response;
};

/**
 *
 * Authorized axios setup - used for requests which required authenticated user
 *
 */
const authorizedAxiosInstance = axios.create({
    baseURL: '',
});

authorizedAxiosInstance.interceptors.request.use(
    (config) => ({ ...config, headers: { 'Current-Organization-Id': getCurrentSelectedOrganizationId() } }),
    (error) => errorHandlerResponse(error),
);

authorizedAxiosInstance.interceptors.response.use(
    (response) => successHandler(response),
    (error) => errorHandlerResponse(error),
);

authorizedAxiosInstance.defaults.withCredentials = true;

authorizedAxiosInstance.interceptors.request.use(function (config) {
    return config;
});

export { authorizedAxiosInstance };
